import { useEffect } from 'react';

import { useInteractionContext } from '@atlaskit/react-ufo/interaction-context';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createActionsHook,
	createStateHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type HookStateFunction,
	type StoreActionApi,
} from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useSpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/page-ready-state/index.tsx';

const store = createStore({
	name: 'priority-manager',
	initialState: { tier: 1 },
	actions: {
		setTier:
			(
				tier: number,
			): (({
				setState,
				getState,
			}: StoreActionApi<{
				tier: number;
			}>) => void) =>
			({ setState, getState }) =>
				setState({
					tier: Math.max(getState().tier, tier),
				}),
	},
});

export const usePriorityActions: HookActionsFunction<
	BoundActions<
		{
			tier: number;
		},
		{
			setTier: (tier: number) => ({
				setState,
				getState,
			}: StoreActionApi<{
				tier: number;
			}>) => void;
		}
	>
> = createActionsHook(store);
export const useCurrentPriority: HookStateFunction<number, void> = createStateHook(store, {
	selector: (state) => state.tier,
});

/**
 * defines loading priorities(tiers) for the application
 */
export const loadingPriority = {
	// tier 0 - "early scripts" is outside of this component reach and responsibility
	/**
	 * Tier 1 - initial loading phase, everything is critical
	 */
	critical: 1,
	/**
	 * Tier 2 - app is ready, but some work is still in progress
	 * This is the phase where we can start loading non-critical resources
	 */
	nonCritical: 2,
	/**
	 * Tier 3 - background phase, all work is done, we can load background resources
	 * This is the phase where we can start loading background resources
	 */
	background: 3,
} as const;

/**
 * @deprecated - this is POC component to manage loading priorities in SPA.
 * This component connects SPA and UFO states in a way we can get something useful today
 * Implementation will be changed in the future.
 */
export function PriorityManager(): null {
	const isReady = useSpaStatePageReady();
	const { setTier } = usePriorityActions();
	const interactionContext = useInteractionContext();
	const { addCustomData } = interactionContext ?? {};
	useEffect(() => {
		if (isReady) {
			performance.mark('priority:non-critical-phase');
			setTier(loadingPriority.nonCritical);

			const tm = setTimeout(
				() => {
					if (fg('hot_301836_bg_task_timeout_info')) {
						addCustomData?.({
							bgTaskTimeout: performance.now(),
						});
					}
					setTier(loadingPriority.background);
				},
				expVal('hot_301836_dynamic_bg_task_timeout', 'value', 5000),
			);
			return () => {
				clearTimeout(tm);
			};
		}
	}, [isReady, setTier, addCustomData]);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const onPageReady = () => {
				performance.mark('priority:background-phase');
				setTier(loadingPriority.background);
			};
			window.addEventListener('UFO_FINISH_INTERACTION', onPageReady);
			return () => {
				window.removeEventListener('UFO_FINISH_INTERACTION', onPageReady);
			};
		}
	}, [setTier]);
	return null;
}
